var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
let u = up.util;
up.Change.OpenLayer = class OpenLayer extends up.Change.Addition {
    constructor(options) {
        super(options);
        this.target = options.target;
        this._origin = options.origin;
        this._baseLayer = options.baseLayer;
    }
    getPreflightProps() {
        return {
            mode: this.options.mode,
            context: this._buildLayer().context,
            origin: this.options.origin,
            target: this.target,
            bindLayer: this._baseLayer,
            layer: 'new',
            bindFragments: u.compact([up.fragment.get(':main', { layer: this._baseLayer })]),
            fragments: [],
        };
    }
    execute(responseDoc, onApplicable) {
        this._matchPostflight(responseDoc);
        onApplicable();
        this._createOverlay();
        let renderOtherLayersOnce = u.memoize(() => this._renderOtherLayers(responseDoc));
        let unbindClosing = this.layer.on('up:layer:accepting up:layer:dismissing', renderOtherLayersOnce);
        try {
            let partialResults = [
                this._renderOverlayContent(responseDoc),
                ...renderOtherLayersOnce(),
            ];
            return new up.RenderResult({
                layer: this.layer,
                target: this.target,
                renderOptions: this.options,
                partialResults,
            });
        }
        finally {
            unbindClosing();
        }
    }
    _matchPostflight(responseDoc) {
        if (this.target === ':none') {
            this._content = document.createElement('up-none');
        }
        else {
            this._content = responseDoc.select(this.target);
        }
        if (!this._content || !this._baseLayer.isAlive()) {
            throw new up.CannotMatch();
        }
    }
    _createOverlay() {
        up.puts('up.render()', `Opening element "${this.target}" in new overlay`);
        this._assertOpenEventEmitted();
        this.layer = this._buildLayer();
        this._baseLayer.peel({ history: !this.layer.history, intent: this.options.peel });
        this._baseLayer.saveHistory();
        up.layer.stack.push(this.layer);
        this.layer.createElements();
        this.layer.setupHandlers();
    }
    _renderOverlayContent(responseDoc) {
        this.handleLayerChangeRequests([this._content], this._getNewLocation());
        responseDoc.commitElement(this._content);
        this.layer.setContent(this._content);
        responseDoc.finalizeElement(this._content);
        this._handleHistory();
        this.setReloadAttrs({ newElement: this._content, source: this.options.source });
        let compilePromise = this._compileLayer();
        this._handleFocus();
        this._handleScroll();
        this.layer.state = 'opened';
        this._emitOpenedEvent();
        this.layer.assertAlive();
        let animatePromise = this.layer.startOpenAnimation();
        return {
            fragments: [this._content],
            finished: Promise.all([compilePromise, animatePromise]),
            verifyFinished: () => this.layer.assertAlive()
        };
    }
    _renderOtherLayers(responseDoc) {
        let steps = this._getHungrySteps().other;
        return this.executeSteps({ steps, responseDoc });
    }
    _compileLayer() {
        return __awaiter(this, void 0, void 0, function* () {
            yield up.hello(this.layer.element, Object.assign(Object.assign({}, this.options), { layer: this.layer, dataRoot: this._content }));
        });
    }
    _buildLayer() {
        const beforeNew = (optionsWithLayerDefaults) => {
            return this.options = up.RenderOptions.finalize(optionsWithLayerDefaults);
        };
        return up.layer.build(this.options, beforeNew);
    }
    _handleHistory() {
        var _a;
        if (this.layer.history === 'auto') {
            this.layer.history = up.fragment.hasAutoHistory([this._content], this.layer);
        }
        (_a = this.layer).history && (_a.history = this._baseLayer.history);
        this.layer.updateHistory(this._getEffectiveHistoryOptions());
    }
    _handleFocus() {
        var _a;
        (_a = this._baseLayer.overlayFocus) === null || _a === void 0 ? void 0 : _a.moveToBack();
        this.layer.overlayFocus.moveToFront();
        const fragmentFocus = new up.FragmentFocus({
            fragment: this._content,
            layer: this.layer,
            autoMeans: ['autofocus', 'layer'],
            inputDevice: this.options.inputDevice,
        });
        fragmentFocus.process(this.options.focus);
    }
    _handleScroll() {
        var _a;
        const scrollingOptions = Object.assign(Object.assign({}, this.options), { fragment: this._content, layer: this.layer, autoMeans: ['hash', 'layer'] });
        const scrolling = new up.FragmentScrolling(scrollingOptions);
        scrolling.process((_a = this.options.scrollMap) !== null && _a !== void 0 ? _a : this.options.scroll);
    }
    _assertOpenEventEmitted() {
        up.event.assertEmitted('up:layer:open', {
            origin: this._origin,
            baseLayer: this._baseLayer,
            layerOptions: this.options,
            log: "Opening new overlay"
        });
    }
    _emitOpenedEvent() {
        this.layer.emit('up:layer:opened', {
            origin: this._origin,
            callback: this.layer.callback('onOpened'),
            log: `Opened new ${this.layer}`
        });
    }
    _getHungrySteps() {
        return up.radio.hungrySteps(this._getEffectiveRenderOptions());
    }
    _getEffectiveRenderOptions() {
        return Object.assign(Object.assign({}, this.options), { layer: this.layer, history: this.layer.history });
    }
    _getEffectiveHistoryOptions() {
        return up.history.options(this.options);
    }
    _getNewLocation() {
        return this._getEffectiveRenderOptions().location;
    }
};
